/**
 * @fileoverview Hook that provides primary navigation links configuration and responsive menu handling
 * @module features/primaryNavigation/routes/usePrimaryNavLinks
 */
import React from 'react';
import { PrimaryNavLink } from 'basecamp';
import { useResponsiveMenu, useWindowSize } from '../../../utils/hooks';
import { translate } from '../../../utils/locale';
import { useSelector } from 'react-redux';
import { getFeature } from 'features/app/redux/features';

/**
 * Custom hook that manages primary navigation links and responsive menu behaviour
 * @returns {Object} Object containing large and small screen menu configurations
 * @returns {Array} largeScreenMenu - Array of menu items for large screen display
 * @returns {Array} smallScreenMenu - Array of menu items for small screen display
 */
const useKs4PerformanceMeasuresNavLinks = () => {
	const canViewKS4PerfMeasures = useSelector(getFeature('ks4_perf_measures'));
	const canViewProgress8Measures = useSelector(getFeature('ks4_progress_8'));

	/**
	 * Configuration array for all primary navigation links
	 * Each object contains name, width, visibility, menu order and component details
	 */
	const ks4PerformanceMeasuresNavLinks = [
		{
			name: 'ks4AttainmentMeasures',
			width: 110,
			isVisible: canViewKS4PerfMeasures,
			menuTransferOrder: 2,
			component: (
				<PrimaryNavLink
					setAs="secondary"
					data-test={`primary-nav-ks4-attainment-measures-link`}
					withInternalUrl={`/performance-measures/attainment-measures`}
					disabled={!canViewKS4PerfMeasures}
				>
					{translate('primaryNavigation.connect.ks4Measures.ATTAINMENT')}
				</PrimaryNavLink>
			),
		},
		{
			name: 'ks4Progress8Measures',
			width: 110,
			isVisible: canViewProgress8Measures,
			menuTransferOrder: 1,
			component: (
				<PrimaryNavLink
					setAs="secondary"
					data-test={`primary-nav-ks4-progress-8-measures-link`}
					withInternalUrl={`/performance-measures/progress-8-measures`}
					onClick={() => {
						console.log('clicked');
					}}
					disabled={!canViewProgress8Measures}
				>
					{translate('primaryNavigation.connect.ks4Measures.PROGRESS')}
				</PrimaryNavLink>
			),
		},
	];

	/**
	 * Get the current window width to determine responsive menu breakpoints
	 * @type {number} width - Current window width in pixels
	 */
	const { width } = useWindowSize();

	// Generate responsive menus based on screen size and dependencies
	// Uses subjectsNavLinks array to create large and small screen menu variants
	// Dependencies array ensures menu updates when relevant data changes
	const { largeScreenMenu, smallScreenMenu } = useResponsiveMenu(
		ks4PerformanceMeasuresNavLinks,
		width,
		0,
		[]
	);

	return { largeScreenMenu, smallScreenMenu };
};

export default useKs4PerformanceMeasuresNavLinks;
